/** MFA enrollment + org enforcement + log-out-all, shown in Settings. */
import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Loader2, KeyRound, LogOut } from "lucide-react";

export const SecuritySettings = () => {
  const { user, refresh } = useAuth();
  const [step, setStep] = useState("");            // "", "setup"
  const [qr, setQr] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState("");
  const isOwner = !user?.is_team_member;

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/mfa/setup");
      setQr(r.data.qr); setSecret(r.data.secret); setStep("setup");
    } catch { toast.error("Could not start MFA setup"); }
    finally { setBusy(false); }
  };

  const enable = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/mfa/enable", { code });
      setBackup(r.data.backup_codes);
      setStep(""); setCode("");
      await refresh();
      toast.success("Two-factor authentication is on");
    } catch (e) { toast.error(e?.response?.data?.detail || "Incorrect code"); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { password: pw });
      setPw("");
      await refresh();
      toast.success("Two-factor authentication turned off");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not disable"); }
    finally { setBusy(false); }
  };

  const toggleEnforce = async (val) => {
    try {
      await api.put("/auth/mfa/org-enforcement", { enforce: val });
      await refresh();
      toast.success(val ? "MFA now required for your team" : "MFA requirement removed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const logoutAll = async () => {
    if (!window.confirm("Sign out of every device? You'll stay signed in here.")) return;
    try {
      const r = await api.post("/auth/logout-all-devices");
      localStorage.setItem("psc_token", r.data.token);
      toast.success("Signed out of all other devices");
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="border-2 border-slate-200 bg-white" data-testid="security-settings-card">
      <div className="px-5 py-4 border-b-2 border-slate-200 bg-slate-50 flex items-center gap-2">
        <KeyRound size={16} className="text-teal-700" />
        <h2 className="font-black text-slate-900 text-sm">Account security</h2>
        {user?.mfa_enabled ? (
          <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-300" data-testid="mfa-status-badge">
            <ShieldCheck size={11} /> 2FA on
          </span>
        ) : (
          <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-300" data-testid="mfa-status-badge">
            <ShieldOff size={11} /> 2FA off
          </span>
        )}
      </div>
      <div className="p-5 space-y-5">
        <p className="text-xs text-slate-500 leading-relaxed">
          Two-factor authentication (2FA) adds a one-time code from an authenticator app
          (Google Authenticator, Authy, Microsoft Authenticator) on top of your password.
        </p>

        {backup && (
          <div className="border-2 border-teal-300 bg-teal-50 p-4" data-testid="backup-codes">
            <p className="text-sm font-bold text-teal-900 mb-2">Save your backup codes</p>
            <p className="text-xs text-teal-800 mb-3">Each code works once if you lose your authenticator. Store them somewhere safe — they won't be shown again.</p>
            <div className="grid grid-cols-2 gap-1.5 font-mono text-sm">
              {backup.map((b) => <span key={b} className="bg-white border border-teal-200 px-2 py-1">{b}</span>)}
            </div>
            <button onClick={() => setBackup(null)} className="mt-3 text-xs font-bold text-teal-800 underline">I've saved them</button>
          </div>
        )}

        {!user?.mfa_enabled && step === "" && (
          <button data-testid="mfa-setup-btn" onClick={startSetup} disabled={busy}
            className="inline-flex items-center gap-2 px-4 h-10 bg-[#1E1B4B] text-white text-sm font-bold hover:bg-[#312E81] disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Set up 2FA
          </button>
        )}

        {step === "setup" && (
          <div className="space-y-3" data-testid="mfa-setup-panel">
            <p className="text-sm text-slate-700 font-semibold">1. Scan this QR code in your authenticator app</p>
            {qr && <img src={qr} alt="MFA QR code" width={168} height={168} className="border-2 border-slate-200" />}
            <p className="text-xs text-slate-500">Can't scan? Enter this key manually: <code className="bg-slate-100 px-1 font-mono">{secret}</code></p>
            <p className="text-sm text-slate-700 font-semibold">2. Enter the 6-digit code it shows</p>
            <div className="flex gap-2">
              <input data-testid="mfa-enable-code" value={code} onChange={(e) => setCode(e.target.value)}
                maxLength={6} placeholder="123456" className="h-10 w-32 px-3 border-2 border-slate-200 font-mono tracking-widest" />
              <button data-testid="mfa-enable-btn" onClick={enable} disabled={busy || code.length !== 6}
                className="px-4 h-10 bg-teal-700 text-white text-sm font-bold hover:bg-teal-800 disabled:opacity-50">
                {busy ? <Loader2 size={14} className="animate-spin" /> : "Turn on 2FA"}
              </button>
              <button onClick={() => { setStep(""); setCode(""); }} className="px-3 h-10 border-2 border-slate-300 text-slate-600 text-sm font-bold">Cancel</button>
            </div>
          </div>
        )}

        {user?.mfa_enabled && (
          <div className="flex flex-wrap items-end gap-2" data-testid="mfa-disable-panel">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Confirm password to turn off</span>
              <input data-testid="mfa-disable-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                className="mt-1 h-10 w-56 px-3 border-2 border-slate-200 text-sm" />
            </label>
            <button data-testid="mfa-disable-btn" onClick={disable} disabled={busy || !pw}
              className="px-4 h-10 border-2 border-red-200 text-red-700 text-sm font-bold hover:bg-red-50 disabled:opacity-50">
              Turn off 2FA
            </button>
          </div>
        )}

        {isOwner && (
          <label className="flex items-center gap-2 pt-2 border-t-2 border-slate-100 mt-2" data-testid="mfa-org-enforce">
            <input type="checkbox" checked={!!user?.mfa_org_enforced}
              onChange={(e) => toggleEnforce(e.target.checked)} className="w-4 h-4" />
            <span className="text-sm text-slate-700 font-semibold">Require 2FA for everyone in my organisation</span>
          </label>
        )}

        <div className="pt-2 border-t-2 border-slate-100">
          <button data-testid="logout-all-btn" onClick={logoutAll}
            className="inline-flex items-center gap-2 px-4 h-9 border-2 border-slate-300 text-slate-700 text-xs font-bold hover:bg-slate-100">
            <LogOut size={13} /> Sign out of all other devices
          </button>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;
